/* eslint-disable indent */
import {
  makeAttachmentTemplate,
  makeDateBodyTemplate,
} from '@src/main/modules/messages/utils/templates';
import { PunctualityReport } from '@src/main/modules/reports/shared/domain/report.interface';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { ColumnFilterElementTemplateOptions } from 'primereact/column';
import { ReactNode } from 'react';

type BaseColumnOptions = {
  field: keyof PunctualityReport;
  labelKey: string;
  minWidth?: string;
  isDate?: boolean;
  dateOnly?: boolean;
  dateFilterTemplate?: (options: ColumnFilterElementTemplateOptions) => ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bodyTemplate?: (rowData: any) => ReactNode;
  sortable?: boolean;
};

// Date-only template (DD/MM/YYYY)
const makeDateOnlyBodyTemplate =
  <T extends object>(field: keyof T) =>
  (rowData: T) => {
    const dateValue = rowData[field] as unknown as Date | string | undefined;
    const date = dateValue ? new Date(dateValue) : null;
    return !date || Number.isNaN(date.getTime()) ? undefined : format(date, 'dd/MM/yyyy');
  };

const createBaseColumn = (
  t: TFunction,
  {
    field,
    labelKey,
    minWidth = '10rem',
    isDate = false,
    dateOnly = false,
    dateFilterTemplate,
    bodyTemplate,
    sortable = true,
  }: BaseColumnOptions,
) => {
  const base: Record<string, unknown> = {
    field,
    header: t(`reports.${labelKey}`),
    filter: true,
    filterField: field,
    showFilterOperator: false,
    showAddButton: false,
    showFilterMatchModes: false,
    style: { minWidth },
    sortable,
  };

  if (isDate) {
    base.dataType = 'date';
    base.body =
      bodyTemplate ??
      (dateOnly
        ? makeDateOnlyBodyTemplate<PunctualityReport>(field)
        : makeDateBodyTemplate<PunctualityReport>(field));
    base.filterElement = dateFilterTemplate;
  }

  if (bodyTemplate && !isDate) {
    base.body = bodyTemplate;
  }

  return base;
};

export const getFlightDateColumnProps = (
  t: TFunction,
  dateFilterTemplate: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'flightDate',
    labelKey: 'flightDate',
    minWidth: '12rem',
    isDate: true,
    dateOnly: true,
    dateFilterTemplate,
  });

export const getCarrierCodeColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'carrierCode', labelKey: 'carrier' });

export const getFlightNumberColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'flightNumber', labelKey: 'flightNumber' });

export const getDepartureAirportColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'departureAirport', labelKey: 'departure' });

export const getArrivalAirportColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'arrivalAirport', labelKey: 'arrival' });

export const getAircraftRegistrationColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'aircraftRegistration', labelKey: 'registration' });

export const getEmployeeIdColumnProps = (t: TFunction) =>
  createBaseColumn(t, { field: 'employeeId', labelKey: 'employeeId' });

export const getUpdatedAtColumnProps = (
  t: TFunction,
  dateFilterTemplate: (options: ColumnFilterElementTemplateOptions) => ReactNode,
) =>
  createBaseColumn(t, {
    field: 'updatedAt',
    labelKey: 'updatedAt',
    minWidth: '12rem',
    isDate: true,
    dateFilterTemplate,
  });

export const getAttachmentColumnProps = <T extends Record<string, unknown>>(
  t: TFunction,
  download: (item: T) => void,
) =>
  createBaseColumn(t, {
    field: 'id',
    labelKey: 'attachment',
    bodyTemplate: makeAttachmentTemplate<T>(t, download),
    sortable: false,
  });
